import NextLink from 'next/link'
import { formatEther } from 'viem'

import AvaxReward from '../Rewards/AvaxReward'
import GGPReward from '../Rewards/GGPReward'

import { Tooltip } from '@/common/components/Tooltip'
import { useValueTokens } from '@/contexts/VaultTokensContext'
import { CoinMark } from '@/design-system/CoinMark'
import { Panel, StatRow } from '@/design-system/ui'
import { useCoinGeckoPrices } from '@/hooks/useCoinGeckoPrices'
import {
  useAPYinBasis,
  useUnderlyingBalance,
  useVaultCap,
  useVaultRatio,
  useVaultTotalAssets,
} from '@/hooks/useGetVaultData'
import { compactNumber } from '@/utils/numberFormatter'

const TAGLINE: Record<string, string> = {
  avax: 'Earn yield on idle AVAX. Deposit to receive seaAVAX, a liquid share token that grows as the vault runs a principal-protected yield strategy on Avalanche. No lockups; redeem whenever the pool has liquidity.',
  ggp: 'Earn yield on idle GGP. Hold xGGP, a share token that grows as the vault’s validators earn. GoGoPool is winding down, so deposits are closed; withdrawals stay open.',
}

/* Translucent white pill — reads as a badge on the orange hero. */
function HeroPill({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center rounded-full"
      style={{
        background: 'rgba(255,255,255,.16)',
        color: '#fff',
        fontFamily: 'var(--font-nav)',
        fontSize: 13,
        fontWeight: 400,
        padding: '4px 12px',
      }}
    >
      {children}
    </span>
  )
}

/* A stat label with an optional tooltip info marker, sized for StatRow. */
function StatLabel({ children, tooltip }: { children: React.ReactNode; tooltip?: string }) {
  if (!tooltip) return <>{children}</>
  return (
    <span className="inline-flex items-center gap-1">
      {children}
      <Tooltip content={tooltip} placement="top">
        <span
          aria-label={tooltip}
          className="inline-flex flex-none"
          style={{ color: 'var(--muted)', cursor: 'help' }}
        >
          <svg
            aria-hidden="true"
            fill="none"
            height="15"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.9"
            viewBox="0 0 24 24"
            width="15"
          >
            <circle cx="12" cy="12" r="9.5" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        </span>
      </Tooltip>
    </span>
  )
}

/**
 * VaultHero — the full-width orange gradient banner (identity left, glass APY card right).
 * Rendered across the top of the detail page, above the two-column action zone.
 */
export function VaultHero() {
  const { currentToken } = useValueTokens()
  const { data: apy } = useAPYinBasis()

  if (!currentToken) return null

  const symbol = currentToken.displayName
  const shareSymbol = currentToken.value === 'avax' ? 'seaAVAX' : `x${symbol}`
  const tokenAddress = currentToken.tokenAddresses[1][43114] as string
  const apyText = `${(apy ? Number(apy) / 100 : 0).toFixed(2)}%`

  // Shared hero banner for every vault (soft dark wash on the left so the white identity text
  // stays legible over the image).
  const heroBackground =
    "radial-gradient(90% 200% at 3% 0%, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 48%), url('/assets/img/banners/hero-vault.webp')"

  return (
    <div
      className="lg:px-10 lg:py-8 lg:gap-8 flex flex-wrap items-end justify-between gap-6 p-6"
      style={{
        background: heroBackground,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        borderRadius: 32,
        color: '#fff',
      }}
    >
      {/* identity */}
      <div>
        <div
          className="flex items-center gap-2"
          style={{ color: 'rgba(255,255,255,.82)', fontFamily: 'var(--font-nav)', fontSize: 16 }}
        >
          <NextLink className="no-underline hover:underline" href="/" style={{ color: 'inherit' }}>
            Vaults
          </NextLink>
          <span>/</span>
          <span>{currentToken.name}</span>
        </div>

        <div className="mt-3 flex items-center gap-4">
          <CoinMark size={56} token={currentToken.value} variant="light" />
          <h1
            style={{
              color: '#fff',
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: 400,
              letterSpacing: 0,
              lineHeight: 1,
            }}
          >
            {currentToken.name}
          </h1>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <HeroPill>Non-custodial</HeroPill>
          <HeroPill>ERC-4626</HeroPill>
          <HeroPill>Audited</HeroPill>
          <a
            className="inline-flex items-center gap-2 no-underline"
            href={`https://snowtrace.io/token/${tokenAddress}`}
            rel="noopener noreferrer"
            style={{ color: 'rgba(255,255,255,.9)', fontSize: 14 }}
            target="_blank"
          >
            <span
              aria-hidden="true"
              className="inline-block rounded-full"
              style={{ background: '#fff', height: 8, width: 8 }}
            />
            {shareSymbol}
            <svg
              aria-hidden="true"
              fill="none"
              height="14"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="14"
            >
              <path d="M7 17 17 7M9 7h8v8" />
            </svg>
          </a>
        </div>
      </div>

      {/* glass APY card — full width on mobile (respecting the hero padding), auto beside the
          identity on wider screens */}
      <div
        className="flex w-full flex-col justify-center md:w-auto"
        style={{
          background: 'rgba(255,255,255,.09)',
          borderRadius: 24,
          minWidth: 210,
          padding: '20px 32px',
        }}
      >
        <div
          style={{
            color: 'rgba(255,255,255,.82)',
            fontFamily: 'var(--font-nav)',
            fontSize: 12,
            fontWeight: 400,
          }}
        >
          Current APY
        </div>
        <div
          className="tabular-nums"
          style={{ fontSize: 40, fontWeight: 400, letterSpacing: 0, lineHeight: 1, marginTop: 8 }}
        >
          {apy === undefined ? '—' : apyText}
        </div>
        <div style={{ color: 'rgba(255,255,255,.72)', fontSize: 12, marginTop: 8 }}>
          Net of fees, updated on-chain each cycle
        </div>
      </div>
    </div>
  )
}

/**
 * VaultStats — the vault's context (tagline, per-figure stat cards, reward countdown). Rendered in
 * the LEFT column of the action zone, beside the sticky deposit rail — so the rail stays pinned
 * while the user scrolls this context, matching the Treasury layout.
 */
export function VaultStats() {
  const { currentToken } = useValueTokens()
  const { data: vaultTotalAssets } = useVaultTotalAssets()
  const { data: vaultCap } = useVaultCap()
  const { data: underlyingBalance } = useUnderlyingBalance()
  const { data: ratio } = useVaultRatio()
  const { prices } = useCoinGeckoPrices()

  if (!currentToken) return null

  const symbol = currentToken.displayName
  const shareSymbol = currentToken.value === 'avax' ? 'seaAVAX' : `x${symbol}`

  const priceKey = currentToken.value === 'ggp' ? 'gogopool' : 'avalanche-2'
  const usdPrice = prices?.[priceKey]?.usd

  const tvlNum = vaultTotalAssets ? Number(formatEther(vaultTotalAssets)) : null
  const tvlUsdNum = tvlNum != null && usdPrice ? tvlNum * usdPrice : null
  const liqNum = underlyingBalance ? Number(formatEther(underlyingBalance)) : null
  const sharePrice = ratio ? Number(formatEther(ratio)).toFixed(4) : '—'
  const capNum = vaultCap ? Number(formatEther(vaultCap)) : null
  const utilPct =
    vaultCap && vaultCap > 0n && vaultTotalAssets
      ? Number((vaultTotalAssets * 10000n) / vaultCap) / 100
      : 0

  return (
    <div className="lg:gap-8 flex flex-col gap-6">
      <p style={{ color: 'var(--muted)', fontSize: 16, lineHeight: 1.6, maxWidth: '60ch' }}>
        {TAGLINE[currentToken.value]}
      </p>

      {/* stats — each figure in its own card */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Panel>
          <StatRow
            label="TVL"
            sub={tvlUsdNum != null ? `$${compactNumber(tvlUsdNum)}` : symbol}
            value={tvlNum != null ? compactNumber(tvlNum) : '—'}
          />
        </Panel>
        <Panel>
          <StatRow
            label={
              <StatLabel tooltip="Assets available to redeem instantly. The pool refills each cycle as validators spin down.">
                Liquidity
              </StatLabel>
            }
            sub={`${symbol} free now`}
            value={liqNum != null ? compactNumber(liqNum) : '—'}
          />
        </Panel>
        <Panel>
          <StatRow
            label={
              <StatLabel
                tooltip={`How much ${symbol} one ${shareSymbol} is worth. It only rises as the vault earns.`}
              >
                Share price
              </StatLabel>
            }
            sub={`${symbol}/${shareSymbol}`}
            value={sharePrice}
          />
        </Panel>
        <Panel>
          <StatRow
            label="Vault cap"
            sub={`${utilPct.toFixed(0)}% full`}
            value={capNum != null ? compactNumber(capNum) : '—'}
          />
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full"
            style={{ background: 'var(--surface-quiet)' }}
          >
            <div
              className="h-full rounded-full"
              style={{
                background: 'var(--ink)',
                transition: 'width .6s cubic-bezier(.22,1,.36,1)',
                width: `${Math.max(2, Math.min(100, utilPct))}%`,
              }}
            />
          </div>
        </Panel>
      </div>

      {/* Reward countdown renders null when there's no reward data — rendered directly (no fixed-
          height wrapper) so it collapses cleanly instead of reserving an empty band. */}
      {currentToken.value === 'ggp' ? <GGPReward /> : <AvaxReward />}
    </div>
  )
}
